import React, { useEffect, useRef, useState } from "react";
import { Xmark, ArrowLeft, CircleCheck, Circle, Flame, ChevronRight, Copy, Check } from "@gravity-ui/icons";
import { useAchievement } from "../../contexts/AchievementContext";
import { useLocalState } from "../../contexts/LocalStateProvider";
import * as Sentry from "@sentry/react"
// <<< EDIT: Add utility function to convert Buffer to Data URL >>>
const bufferToDataURL = (bufferData, mimeType) => {
  // Check if bufferData is valid
  if (!bufferData || !Array.isArray(bufferData)) {
    console.error("Invalid buffer data provided to bufferToDataURL");
    return null; // Or return a default placeholder URL
  }
  try {
    // Convert Array/Buffer data to a binary string
    // Note: Using reduce for potentially better performance with large arrays
    const binary = bufferData.reduce((str, byte) => str + String.fromCharCode(byte), '');
    // Encode the binary string to Base64
    const base64 = btoa(binary);
    return `data:${mimeType};base64,${base64}`;
  } catch (error) {
    console.error("Error converting buffer to Data URL:", error);
    Sentry.captureException(error);
    return null; // Or return a default placeholder URL
  }
};

export default function AchievementCard({
    trigger = true,
    milestone = "Milestone 1",
    onClose,
    milestoneData
}) {
    const [isVisible, setIsVisible] = useState(trigger);
    const modalRef = useRef(null);
    const [showSocialLinks, setShowSocialLinks] = useState(false);
    const [copied, setCopied] = useState(false);
    const { localState } = useLocalState();

    //console.log("AchievementModal localState:", localState);
    //console.log("AchievementModal profileImage source:", localState?.userProfileData?.user_avatar);

    // <<< EDIT: Logic to handle Buffer or fallback >>>
    const avatarData = localState?.userProfileData?.user_avatar;
    let profileImage = "https://avatar.iran.liara.run/public"; // Default placeholder

    // Keep this log to see the raw data
    //console.log("AchievementModal profileImage source raw:", avatarData);

    if (avatarData && avatarData.type === 'Buffer' && Array.isArray(avatarData.data)) {
        // Buffer detected! Attempt to convert to Data URL.
        // IMPORTANT: Assumes MIME type might be stored separately, or defaults to 'image/jpeg'.
        // You might need to adjust 'image/jpeg' if your avatars are PNG or other types,
        // or ideally, store the correct MIME type in localState alongside the buffer.
        const mimeType = localState?.userProfileData?.user_avatar_mimetype || 'image/jpeg';
        if (!localState?.userProfileData?.user_avatar_mimetype) {
            //console.warn("AchievementModal: MIME type for user avatar not found in localState, defaulting to 'image/jpeg'. Please ensure 'user_avatar_mimetype' is stored in localState.userProfileData if possible.");
        }
        const dataUrl = bufferToDataURL(avatarData.data, mimeType);
        if (dataUrl) {
            profileImage = dataUrl; // Use the generated Data URL
        } else {
             console.error("Failed to convert buffer to Data URL, using placeholder.");
        }
    } else if (typeof avatarData === 'string' && avatarData.startsWith('http')) {
         // Handle case where it might sometimes be a URL string (less likely now)
         profileImage = avatarData;
    }
    // If avatarData is null, undefined, or not a Buffer/string, profileImage remains the placeholder.

    const shareText = "I just got a gift from Wubble AI. Sign up and get yours too!";
    const shareUrl = window.location.origin;

    const encodedShareUrl = encodeURIComponent(shareUrl);
    const encodedTextWithUrl = encodeURIComponent(`${shareText} ${shareUrl}`);
    const encodedBaseText = encodeURIComponent(shareText);

    const twitterShareUrl = `https://twitter.com/intent/tweet?url=${encodedShareUrl}&text=${encodedBaseText}`;
    const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedShareUrl}&quote=${encodedBaseText}`;
    const linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedShareUrl}`;
    const telegramShareUrl = `https://t.me/share/url?url=${encodedShareUrl}&text=${encodedBaseText}`;
    const whatsappShareUrl = `https://api.whatsapp.com/send?text=${encodedTextWithUrl}`;

    const onKeepCreating = () => {
        setIsVisible(false);
        setShowSocialLinks(false);
        if (onClose) onClose();
    };

    const onShare = () => {
        setShowSocialLinks(true);
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        setCopied(true);
        setTimeout(() => {
            setCopied(false);
        }, 2000);
    };

    const handleClose = () => {
        setIsVisible(false);
        setShowSocialLinks(false);
        setTimeout(() => {
            if (onClose) onClose();
        }, 300);
    };

    useEffect(() => {
        setIsVisible(trigger);
        if (!trigger) {
            setShowSocialLinks(false);
        }
    }, [trigger]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                handleClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    if (!isVisible) return null;

    const RingedProfile = ({ imageUrl }) => (
        <div className="relative w-24 h-24 flex items-center justify-center">
            <div className="absolute w-24 h-24 rounded-full border-4 border-white/50" />
            <div className="relative z-20 w-20 h-20 rounded-full overflow-hidden">
                <img
                    src={imageUrl}
                    alt="Profile"
                    className="w-full h-full object-cover"
                />
            </div>
            <div className="absolute bottom-0 rounded-full flex items-center justify-center bg-gradient-to-r from-[#fc10f2] to-[#3078e4] right-0 w-8 h-8 z-30">
                <img
                    src="/UpdateMinutes.svg"
                    alt="Milestone1"
                    className="w-5"
                />
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-50 md:w-screen md:h-screen p-3">
            <div
                ref={modalRef}
                className="w-full max-w-md overflow-hidden rounded-lg border border-white/10 bg-[#1D1B1D] transform translate-y-4 sm:translate-y-0"
            >
                <div className="relative h-52 bg-gradient-to-r from-[#7C0CA2] to-[#FA6420] p-2">
                    <div className="absolute inset-0 overflow-hidden">
                    <img src="/Celebrate.gif" alt="Celebration GIF" className="object-cover w-full"/>
                    </div>

                    <div className="relative mx-auto w-full h-full flex items-center justify-center">
                        <RingedProfile imageUrl={profileImage} />
                        
                    </div>
                </div>

                <div className="bg-[#18181b] px-3 pt-4 pb-5 text-center">
                    <h2 className="mb-1 text-2xl font-normal text-white">Congratulations!</h2>
                    <p className="mb-4 text-base font-extralight text-white/80">
                        You have earned {' '}
                        {(() => {
                            let highlightedText = '';
                            const remainingText = ' of generation power';
                            let isKnownMilestone = true;

                            if (milestone === 'Milestone 1' || milestone === '5 Minutes Milestone') {
                                highlightedText = '5 minutes';
                            } else if (milestone === 'Milestone 2' || milestone === '10 Minutes Milestone') {
                                highlightedText = '10 minutes';
                            } else if (milestone === 'Milestone 3' || milestone === '15 Minutes Milestone') {
                                highlightedText = '15 minutes';
                            } else {
                                isKnownMilestone = false;
                            }

                            if (isKnownMilestone) {
                                return (
                                    <>
                                        <span className="bg-gradient-to-r from-[#fc10f2] to-[#3078e4] bg-clip-text text-transparent font-normal">{highlightedText}</span>
                                        <span className="font-normal text-white">{remainingText}</span>
                                    </>
                                );
                            } else {
                                return <span className="font-normal text-white">[{milestone}]</span>; // Fallback
                            }
                        })()}
                    </p>

                    {!showSocialLinks && (
                        <div className="md:flex md:flex-row flex-col-reverse flex justify-center gap-2 items-center">
                            <button
                                className="rounded-full px-3 h-9 text-sm text-[#878188] flex items-center justify-center hover:bg-white/5 hover:text-white transition-colors"
                                onClick={onKeepCreating}
                            >
                                Keep creating
                            </button>

                            <button
                                className="rounded-full text-sm bg-gradient-to-r from-[#fc10f2] to-[#3078e4] px-3 h-9 flex items-center justify-center text-white hover:to-[#fc10f2] hover:from-[#3078e4] transition-colors"
                                onClick={onShare}
                            >
                                Share your achievement
                            </button>
                        </div>
                    )}

                    {showSocialLinks && (
                         <div className="flex flex-col items-center gap-4 pt-2">
                            <div className="flex items-center w-full max-w-xs">
                                 <div className="flex w-full justify-between rounded-full pl-3 pr-0.5 items-center h-9 text-sm font-extralight text-white/40 truncate border border-[#4E4A4F]">
                                     <span className="w-full max-w-60 truncate" title={`${shareText} ${shareUrl}`}>
                                         {`${shareText} ${shareUrl}`}
                                     </span>
                                     <button
                                         onClick={handleCopyLink}
                                         className="flex items-center justify-center hover:bg-white/10 hover:text-white bg-white/5 h-8 w-8 rounded-full"
                                     >
                                         {copied ? (
                                             <Check className="w-4 h-4 text-white"/>
                                         ) : (
                                             <Copy className="w-4 h-4"/>
                                         )}
                                     </button>
                                 </div>
                             </div>
                             <div className="flex justify-center gap-3 w-full max-w-xs">
                                 <a href={facebookShareUrl} target="_blank" rel="noopener noreferrer" className="p-px h-10 w-10 flex items-center justify-center hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] rounded-full transition duration-500">
                                     <button className="flex items-center justify-center w-9 h-9 rounded-full bg-[#2c2a2c] hover:bg-[#3C393C]">
                                         <img src="/Facebook.svg" alt="Share on Facebook" className="w-5 h-5"/>
                                     </button>
                                 </a>
                                 <a href={twitterShareUrl} target="_blank" rel="noopener noreferrer" className="p-px h-10 w-10 flex items-center justify-center hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] rounded-full transition duration-500">
                                     <button className="flex items-center justify-center w-9 h-9 rounded-full bg-[#2c2a2c] hover:bg-[#3C393C]">
                                         <img src="/X.svg" alt="Share on X" className="w-5 h-5"/>
                                     </button>
                                 </a>
                                 <a href={linkedinShareUrl} target="_blank" rel="noopener noreferrer" className="p-px h-10 w-10 flex items-center justify-center hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] rounded-full transition duration-500">
                                     <button className="flex items-center justify-center w-9 h-9 rounded-full bg-[#2c2a2c] hover:bg-[#3C393C]">
                                         <img src="/Linkedin.svg" alt="Share on LinkedIn" className="w-5 h-5"/>
                                     </button>
                                 </a>
                                 <a href={telegramShareUrl} target="_blank" rel="noopener noreferrer" className="p-px h-10 w-10 flex items-center justify-center hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] rounded-full transition duration-500">
                                     <button className="flex items-center justify-center w-9 h-9 rounded-full bg-[#2c2a2c] hover:bg-[#3C393C]">
                                         <img src="/Telegram.svg" alt="Share on Telegram" className="w-5 h-5"/>
                                     </button>
                                 </a>
                                 <a href={whatsappShareUrl} target="_blank" rel="noopener noreferrer" className="p-px h-10 w-10 flex items-center justify-center hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] rounded-full transition duration-500">
                                     <button className="flex items-center justify-center w-9 h-9 rounded-full bg-[#2c2a2c] hover:bg-[#3C393C]">
                                         <img src="/Whatsapp.svg" alt="Share on WhatsApp" className="w-5 h-5"/>
                                     </button>
                                 </a>
                             </div>
                            <button
                                 className="mt-2 rounded-full px-3 h-9 text-sm text-[#878188] flex items-center justify-center hover:bg-white/5 hover:text-white transition-colors"
                                 onClick={() => setShowSocialLinks(false)}
                             >
                                 Back
                             </button>
                         </div>
                    )}
                </div>
            </div>
        </div>
    );
}